// Public assessment page - /assess/:token
// Track A / A7 (6 Sep 2026); rebuilt on branch assessment-ux-2026-09-06 so the page a
// candidate answers on carries the DCS look: cream ground, gold rule, the logo and wordmark,
// the site's type and the vendored ui primitives, phone-first touch targets, one clear
// progress indicator, and a graceful screen for every state the link can be in.
// No login, no OTP: the link token is the credential (7 days, one submission).
// Backend: routes/assessments.py  GET /api/public/assess/{token}, POST .../start, POST .../submit
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Toaster, toast } from 'sonner';
import {
  AlertTriangle, Check, CheckCircle2, ClipboardCheck, ClipboardList, Clock, Loader2,
  RefreshCw, Send, Shield, ShieldCheck, Star,
} from 'lucide-react';

const BASE = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/assess`;

// The one external asset on this page: the DCS mark used by the app sidebar.
const DCS_LOGO = 'https://customer-assets.emergentagent.com/job_talent-match-129/artifacts/10z830dt_Daruwallas%20Consultancy%20Services%20LLP%20Logo.png';
// Brand tokens, same values as index.css (--accent / .text-gold) and the DCS case-file template.
const GOLD = '#C9A54E';
const GOLD_LIGHT = '#E8C96A';
const GOLD_GRADIENT = `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`;
const RULE = 'bg-gradient-to-r from-[#C9A54E] via-[#E8C96A] to-[#C9A54E]';

async function call(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, { headers: { 'Content-Type': 'application/json' }, ...opts });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    const err = new Error((body && body.detail) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// --- brand chrome ------------------------------------------------------------------------------
function BrandBar({ kicker, right }) {
  return (
    <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-[#E5E1D5]" data-testid="dcs-brand-bar">
      <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img
            src={DCS_LOGO}
            alt="Daruwalla Consultancy Services"
            className="h-9 w-auto shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="min-w-0 leading-tight">
            <div className="text-[13px] sm:text-sm font-semibold tracking-tight text-[#0F1729] truncate">
              Daruwalla Consultancy Services
            </div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#C9A54E] truncate">{kicker}</div>
          </div>
        </div>
        {right}
      </div>
      <div className={`h-[3px] w-full ${RULE}`} />
    </header>
  );
}

function BrandFooter() {
  return (
    <footer className="mx-auto w-full max-w-3xl px-4 sm:px-6 pb-10 pt-8 text-center">
      <div className="mx-auto mb-4 h-px w-16 bg-[#E5E1D5]" />
      <p className="text-xs text-slate-500 leading-relaxed">
        Daruwalla Consultancy Services LLP &middot; Fort, Mumbai
        <br />
        Trouble with this page? Write to{' '}
        <a href="mailto:care@daruwallas.com" className="font-medium text-[#8A6D22] underline underline-offset-2">
          care@daruwallas.com
        </a>
      </p>
    </footer>
  );
}

function Shell({ kicker, right, children, wide }) {
  return (
    <div className="min-h-screen bg-[#FDFCF9] text-[#0F1729] flex flex-col">
      <Toaster position="top-center" richColors />
      <BrandBar kicker={kicker} right={right} />
      <main className={`flex-1 mx-auto w-full ${wide ? 'max-w-3xl' : 'max-w-2xl'} px-4 sm:px-6 py-6 sm:py-8`}>{children}</main>
      <BrandFooter />
    </div>
  );
}

function Callout({ children, className = '' }) {
  return (
    <div className={`rounded-lg border border-[#E5E1D5] border-l-4 border-l-[#C9A54E] bg-[#FDFBF7] p-4 ${className}`}>
      {children}
    </div>
  );
}

function GoldButton({ children, className = '', style, ...props }) {
  return (
    <Button
      {...props}
      className={`h-12 w-full rounded-full text-[15px] font-semibold text-white shadow-sm transition-transform hover:brightness-[1.03] active:scale-[0.99] disabled:opacity-50 ${className}`}
      style={{ background: GOLD_GRADIENT, ...style }}
    >
      {children}
    </Button>
  );
}

function QuietButton({ children, className = '', ...props }) {
  return (
    <Button
      variant="outline"
      {...props}
      className={`h-12 rounded-full border-[#E5E1D5] bg-white text-[15px] font-semibold text-[#0F1729] hover:bg-[#FDFBF7] hover:text-[#0F1729] ${className}`}
    >
      {children}
    </Button>
  );
}

// --- answer inputs -----------------------------------------------------------------------------
// One shared row: at least 56px tall, whole row tappable, gold when chosen.
function OptionRow({ selected, multi, label, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={selected}
      className={`flex min-h-[56px] w-full items-center gap-3 rounded-xl border p-3.5 text-left text-[15px] leading-snug transition-colors ${
        selected ? 'border-[#C9A54E] bg-[#FDFBF7] text-[#0F1729]' : 'border-[#E5E1D5] bg-white text-slate-700 hover:bg-[#FDFBF7]'
      }`}
    >
      <span
        className={`grid h-6 w-6 shrink-0 place-items-center border transition-colors ${multi ? 'rounded-md' : 'rounded-full'} ${
          selected ? 'border-[#C9A54E] text-white' : 'border-[#D8CFB6] bg-white'
        }`}
        style={selected ? { background: GOLD_GRADIENT } : undefined}
      >
        {selected && <Check className="h-3.5 w-3.5" />}
      </span>
      <span className="min-w-0 flex-1">{label}</span>
    </button>
  );
}

function RatingInput({ q, value, onChange, qid }) {
  const max = q.max_rating || 5;
  return (
    <div className="flex flex-wrap gap-2" role="group" aria-label={`Rate 1 to ${max}`}>
      {Array.from({ length: max }, (_, i) => i + 1).map((n) => {
        const on = value >= n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-label={`Rate ${n} of ${max}`}
            aria-pressed={value === n}
            data-testid={`rating-${qid}-${n}`}
            className={`flex h-12 min-w-[52px] items-center justify-center gap-1 rounded-xl border text-sm font-semibold transition-colors ${
              value === n ? 'border-[#C9A54E] bg-[#FDFBF7] text-[#8A6D22]' : 'border-[#E5E1D5] bg-white text-slate-600 hover:bg-[#FDFBF7]'
            }`}
          >
            <Star className={`h-4 w-4 ${on ? 'fill-current' : ''}`} style={on ? { color: GOLD } : undefined} />
            {n}
          </button>
        );
      })}
    </div>
  );
}

function QuestionInput({ q, value, onChange }) {
  if (q.kind === 'mcq' && q.multi) {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (id) => onChange(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
    return (
      <div className="space-y-2">
        {(q.options || []).map((o) => (
          <OptionRow key={o.id} multi selected={arr.includes(o.id)} label={o.label} onClick={() => toggle(o.id)}
            testid={`option-${q.id}-${o.id}`} />
        ))}
        <p className="pt-1 text-xs text-slate-500">Select all that apply.</p>
      </div>
    );
  }
  if (q.kind === 'mcq') {
    return (
      <div className="space-y-2">
        {(q.options || []).map((o) => (
          <OptionRow key={o.id} selected={value === o.id} label={o.label} onClick={() => onChange(o.id)}
            testid={`option-${q.id}-${o.id}`} />
        ))}
      </div>
    );
  }
  if (q.kind === 'rating') return <RatingInput q={q} value={value} onChange={onChange} qid={q.id} />;
  const maxChars = q.max_chars || 4000;
  const used = (value || '').length;
  return (
    <div className="space-y-1.5">
      <Textarea
        rows={6}
        value={value || ''}
        maxLength={maxChars}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type your answer…"
        data-testid={`answer-${q.id}`}
        className="min-h-[132px] rounded-xl border-[#E5E1D5] bg-white text-base leading-relaxed focus-visible:ring-[#C9A54E]"
      />
      <p className="text-right text-[11px] tabular-nums text-slate-400">{used} / {maxChars}</p>
    </div>
  );
}

export default function AssessmentTakePage() {
  const { token } = useParams();
  const [stage, setStage] = useState('loading'); // loading | intro | quiz | done | closed
  const [info, setInfo] = useState(null);
  const [closed, setClosed] = useState({ title: '', message: '', kind: 'invalid' });
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [agree, setAgree] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const topRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    call(`/${token}`)
      .then((r) => {
        if (cancelled) return;
        setInfo(r);
        if (r.status === 'submitted') { setResult(r.score); setStage('done'); return; }
        setStage('intro');
      })
      .catch((e) => {
        if (cancelled) return;
        setClosed(
          e.status === 503
            ? { kind: 'off', title: 'Not available just now', message: 'Assessments are switched off at the moment. Please try this link again later, or reply to the message that brought you here.' }
            : e.status === 410
              ? { kind: 'expired', title: 'This link has closed', message: 'It has expired or was withdrawn. If you still want to complete the assessment, ask your Daruwallas contact for a fresh link.' }
              : e.status === 429
                ? { kind: 'busy', title: 'One moment', message: 'That was a lot of requests from your connection. Please wait a minute and reload this page.' }
                : { kind: 'invalid', title: 'This link is not valid', message: 'Please open the exact link from your email or WhatsApp - copying part of it can break it.' },
        );
        setStage('closed');
      });
    return () => { cancelled = true; };
  }, [token]);

  const questions = useMemo(
    () => (info?.assessment?.questions || []).slice().sort((a, b) => (a.order_index || 0) - (b.order_index || 0)),
    [info],
  );

  const isAnswered = useCallback((q) => {
    const v = answers[q.id];
    if (q.kind === 'mcq' && q.multi) return Array.isArray(v) && v.length > 0;
    if (q.kind === 'text') return typeof v === 'string' && v.trim().length > 0;
    return v !== undefined && v !== null && v !== '';
  }, [answers]);

  const answered = useMemo(() => questions.filter(isAnswered).length, [questions, isAnswered]);

  const start = useCallback(async () => {
    setBusy(true);
    try {
      await call(`/${token}/start`, { method: 'POST' });
      setStage('quiz');
      if (topRef.current) topRef.current.scrollIntoView({ block: 'start' });
    } catch (e) {
      if (e.status === 409) { setStage('done'); return; }
      toast.error(e.message);
    } finally { setBusy(false); }
  }, [token]);

  const doSubmit = useCallback(async () => {
    setConfirming(false);
    setBusy(true);
    try {
      const r = await call(`/${token}/submit`, { method: 'POST', body: JSON.stringify({ answers }) });
      setResult(r);
      setStage('done');
      toast.success('Assessment submitted');
    } catch (e) {
      if (e.status === 409) { setStage('done'); return; }
      toast.error(e.message);
    } finally { setBusy(false); }
  }, [token, answers]);

  const submit = useCallback(() => {
    if (answered < questions.length) { setConfirming(true); return; }
    doSubmit();
  }, [answered, questions.length, doSubmit]);

  // ---- loading ---------------------------------------------------------------------------------
  if (stage === 'loading') {
    return (
      <Shell kicker="Candidate assessment">
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="assessment-loading">
          <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
            <Loader2 className="h-7 w-7 animate-spin" style={{ color: GOLD }} />
            <p className="text-sm text-slate-500">Opening your assessment&hellip;</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- closed / expired / off / invalid ---------------------------------------------------------
  if (stage === 'closed') {
    const Icon = closed.kind === 'expired' ? Clock : AlertTriangle;
    return (
      <Shell kicker="Candidate assessment">
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="assessment-closed">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-full bg-[#F5EFDF]">
              <Icon className="h-7 w-7" style={{ color: GOLD }} />
            </div>
            <h1 className="text-xl font-semibold tracking-tight">{closed.title}</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600" data-testid="assessment-closed-message">
              {closed.message}
            </p>
            <QuietButton className="mt-6 w-full sm:w-auto sm:px-8" onClick={() => window.location.reload()}>
              <RefreshCw className="mr-2 h-4 w-4" />Reload
            </QuietButton>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- done ------------------------------------------------------------------------------------
  if (stage === 'done') {
    return (
      <Shell kicker="Candidate assessment" right={<span className="hidden sm:inline text-xs font-medium text-slate-500">Complete</span>}>
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="assessment-done">
          <CardContent className="p-8 text-center">
            <div className="mx-auto mb-4 grid h-16 w-16 place-items-center rounded-full" style={{ background: 'rgba(201,165,78,0.14)' }}>
              <CheckCircle2 className="h-9 w-9" style={{ color: GOLD }} />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Assessment submitted</h1>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-slate-600">
              Thank you{info?.candidate_first_name ? `, ${info.candidate_first_name}` : ''}. Your answers are with the
              Daruwallas team and a consultant will be in touch about next steps.
            </p>
            {result && result.max_score > 0 && (
              <div className="mx-auto mt-6 max-w-xs rounded-xl border border-[#E5E1D5] bg-[#FDFBF7] px-5 py-4" data-testid="assessment-score">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A6D22]">Auto-scored questions</p>
                <p className="mt-1 text-3xl font-semibold tabular-nums text-[#0F1729]">
                  {result.score}<span className="text-lg text-slate-400"> / {result.max_score}</span>
                </p>
                {result.pending_grades > 0 && (
                  <p className="mt-1 text-xs text-slate-500">
                    {result.pending_grades} written answer{result.pending_grades > 1 ? 's' : ''} awaiting review
                  </p>
                )}
              </div>
            )}
            <p className="mt-6 text-xs text-slate-400">You can close this page.</p>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- intro -----------------------------------------------------------------------------------
  if (stage === 'intro') {
    const a = info.assessment;
    const facts = [
      { icon: ClipboardList, text: <><strong>{a.question_count}</strong> question{a.question_count === 1 ? '' : 's'}. There is no timer &mdash; take the time you need, but finish in one sitting.</> },
      { icon: ClipboardCheck, text: <>This is a <strong>single attempt</strong>. Once submitted you cannot change your answers.</> },
      { icon: Clock, text: <>This link expires on {info.expires_at ? new Date(info.expires_at).toLocaleString() : 'the date in your invitation'}.</> },
      { icon: Shield, text: <>Your answers, the time of submission and your network address are recorded and shared with the hiring team.</> },
    ];
    return (
      <Shell kicker="Candidate assessment">
        <Card className="border-[#E5E1D5] shadow-sm" data-testid="assessment-intro-card">
          <CardContent className="space-y-5 p-5 sm:p-7">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#C9A54E]">Assessment</p>
              <h1 className="mt-1.5 text-2xl font-semibold leading-snug tracking-tight sm:text-3xl" data-testid="assessment-title">
                {a.title}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">
                Hi {info.candidate_first_name || 'there'}, Daruwalla Consultancy Services has asked you to complete a
                short assessment as part of your application.
              </p>
            </div>

            {a.description && (
              <Callout>
                <div className="whitespace-pre-line text-sm leading-relaxed text-[#0F1729]">{a.description}</div>
              </Callout>
            )}

            <ul className="space-y-3 text-sm">
              {facts.map(({ icon: Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[#F5EFDF]">
                    <Icon className="h-4 w-4" style={{ color: GOLD }} />
                  </span>
                  <span className="leading-relaxed text-slate-600">{text}</span>
                </li>
              ))}
            </ul>

            <div className="flex items-start gap-3 rounded-lg border border-[#E5E1D5] bg-white p-4">
              <Checkbox
                id="agree"
                checked={agree}
                onCheckedChange={(v) => setAgree(!!v)}
                className="mt-0.5 h-5 w-5 shrink-0 border-[#C9A54E] data-[state=checked]:border-[#C9A54E] data-[state=checked]:bg-[#C9A54E]"
                data-testid="agree-checkbox"
              />
              <Label htmlFor="agree" className="cursor-pointer text-sm leading-relaxed text-slate-700">
                <span className="mb-0.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#8A6D22]">
                  <ShieldCheck className="h-3.5 w-3.5" />Before you start
                </span>
                I understand the conditions above.
              </Label>
            </div>

            <GoldButton disabled={!agree || busy} onClick={start} data-testid="start-assessment-btn">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Start the assessment'}
            </GoldButton>
          </CardContent>
        </Card>
      </Shell>
    );
  }

  // ---- quiz ------------------------------------------------------------------------------------
  const pct = questions.length ? Math.round((answered / questions.length) * 100) : 0;
  return (
    <Shell
      wide
      kicker="Candidate assessment"
      right={
        <span className="shrink-0 rounded-full border border-[#E5E1D5] bg-[#FDFBF7] px-3 py-1 text-[11px] font-semibold tabular-nums text-[#8A6D22]" data-testid="answered-count">
          {answered} / {questions.length}
        </span>
      }
    >
      <div ref={topRef} />

      {/* one progress indicator, plus a dot per question so a long set stays legible on a phone */}
      <div className="mb-5 rounded-xl border border-[#E5E1D5] bg-white p-4 shadow-sm" data-testid="assessment-progress">
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h1 className="truncate text-base font-semibold tracking-tight">{info.assessment.title}</h1>
          <span className="shrink-0 text-xs font-medium tabular-nums text-slate-500">{pct}% answered</span>
        </div>
        <Progress value={pct} className="h-2 bg-[#EFE9DA] [&>*]:bg-[#C9A54E]" />
        {questions.length > 1 && questions.length <= 30 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {questions.map((q, i) => (
              <span
                key={q.id}
                title={`Question ${i + 1}`}
                className="h-1.5 w-5 rounded-full transition-colors"
                style={{ background: isAnswered(q) ? GOLD : '#EFE9DA' }}
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4 pb-4">
        {questions.map((q, i) => (
          <Card key={q.id} className="border-[#E5E1D5] shadow-sm" data-testid={`question-card-${q.id}`}>
            <CardContent className="space-y-4 p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <span
                  className={`mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-[11px] font-bold transition-colors ${
                    isAnswered(q) ? 'text-white' : 'bg-[#EFE9DA] text-[#9C8A5F]'
                  }`}
                  style={isAnswered(q) ? { background: GOLD_GRADIENT } : undefined}
                >
                  {isAnswered(q) ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <h2 className="text-[15px] font-semibold leading-snug sm:text-base">{q.prompt}</h2>
              </div>
              <QuestionInput q={q} value={answers[q.id]} onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))} />
            </CardContent>
          </Card>
        ))}
      </div>

      {confirming && (
        <Card className="mb-3 border-[#C9A54E] bg-[#FDFBF7] shadow-sm" data-testid="submit-confirm">
          <CardContent className="p-5">
            <p className="flex items-start gap-2 text-sm font-medium text-[#0F1729]">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" style={{ color: GOLD }} />
              You have answered {answered} of {questions.length} questions.
            </p>
            <p className="mt-1 pl-6 text-sm leading-relaxed text-slate-600">
              You cannot come back to this assessment once it is submitted. Submit anyway?
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row-reverse">
              <GoldButton className="sm:w-auto sm:px-8" onClick={doSubmit} disabled={busy} data-testid="confirm-submit-btn">
                Yes, submit now
              </GoldButton>
              <QuietButton className="w-full sm:w-auto sm:px-8" onClick={() => setConfirming(false)} data-testid="cancel-submit-btn">
                Keep answering
              </QuietButton>
            </div>
          </CardContent>
        </Card>
      )}

      {/* the submit bar is always within thumb reach on a phone */}
      <div className="sticky bottom-0 -mx-4 border-t border-[#E5E1D5] bg-[#FDFCF9]/95 px-4 py-3 backdrop-blur-sm sm:-mx-6 sm:px-6 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
        <GoldButton disabled={busy} onClick={submit} data-testid="submit-assessment-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Send className="mr-2 h-5 w-5" />Submit assessment</>}
        </GoldButton>
        <p className="mt-2 text-center text-[11px] text-slate-500">
          One attempt only. Check your answers before you submit.
        </p>
      </div>
    </Shell>
  );
}
